import { useEffect, useRef, useState } from 'react'
import { TournamentWSClient } from '../lib/sdk'
import {
  OperationStore,
  ReconciliationManager,
} from '../lib/store'
import { normalizeFastForwardResponse } from '../lib/store/fast-forward-normalizer'
import { filterLocalPlayerOperations, isLocalPlayerEvent } from '../lib/store/operation-owner'
import type { MergedSymbolInfo } from '../lib/chart/dx-trading-mappers'
import { createTournamentProviders } from '../lib/chart/create-tournament-providers'
import { extractChartSymbolInfo } from '../lib/chart/symbol-info'
import { resolveMappedShortSymbol } from '../lib/chart/utils'
import { chartDebugLog } from '../lib/debug/browser-logging'

interface SymbolMaps {
  symbolShortNames: Record<string, string>
  symbolFullNames: Record<string, string>
}

interface BackendSymbol {
  symbol: string
  contractSize: number
  pipSize: string
  maxLots: number
  conversionSymbol?: string
  conversionType?: 'MULTIPLY' | 'DIVIDE' | ''
}

interface TournamentInfoResponse {
  type?: string
  playerPrimarySymbols?: Record<string, string>
  secondarySymbol?: string
  initialChartData?: any
  symbols?: Array<{
    symbol: string
    conversionSymbol?: string
    conversionType?: 'MULTIPLY' | 'DIVIDE' | ''
  }>
  startTime?: string
  endTime?: string
}

export interface AppError {
  type: string
  message: string
  originalError?: Error
}

export interface UseTournamentConnectionResult {
  dependencies: any | null
  error: AppError | null
  isReady: boolean
  /** Incremented on fast-forward to trigger DXChart remount for fresh history load. */
  chartRemountKey: number
}

const ALLOWED_PRICE_TYPES = new Set(['last', 'mark', 'bid', 'ask'])

function parsePositiveIntegerParam(value: string | null): number | undefined {
  if (!value) return undefined
  const parsed = Number(value)
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return undefined
  }
  return Math.floor(parsed)
}

function parsePriceTypesParam(value: string | null): string[] | undefined {
  if (!value) {
    return undefined
  }

  const parsedTypes = value
    .split(',')
    .map((type) => type.trim().toLowerCase())
    .filter((type) => ALLOWED_PRICE_TYPES.has(type))

  if (parsedTypes.length === 0) {
    return undefined
  }

  return Array.from(new Set(parsedTypes))
}

export function useTournamentConnection(): UseTournamentConnectionResult {
  const [result, setResult] = useState<UseTournamentConnectionResult>({
    dependencies: null,
    error: null,
    isReady: false,
    chartRemountKey: 0,
  })
  const chartRemountKeyRef = useRef(0)

  const controllerRef = useRef<AbortController>(new AbortController())
  const unsubscribeStoreRef = useRef<(() => void) | null>(null)
  const wsUnsubscribesRef = useRef<Array<() => void>>([])

  useEffect(() => {
    const controller = new AbortController()
    controllerRef.current = controller
    let cancelled = false

    const initializeConnection = async () => {
      try {
        // Parse URL parameters
        const params = new URLSearchParams(window.location.search)
        const symbolParam = params.get('symbol') || ''
        const tournamentId = params.get('tournamentId') || ''
        const userId = params.get('userId') || ''
        const ticket = params.get('ticket') || ''
        // Fallback chain for WebSocket server domain:
        // 1. wsUrl query parameter (highest priority - most specific to this request)
        // 2. REACT_APP_TOURNAMENT_SERVER_DOMAIN env var (fallback for deployment configuration)
        // 3. localhost:8080 (default for development)
        const serverDomain =
          params.get('wsUrl') || params.get('serverDomain') || process.env.REACT_APP_TOURNAMENT_SERVER_DOMAIN || 'localhost:8080'
        const wsProtocol = (params.get('wsProtocol') as 'ws' | 'wss' | null) || undefined
        const useDemoData = params.get('useDemoData') === 'true'
        const cutoff = params.get('cutoff') ?? undefined
        const queueType = params.get('queueType') ?? undefined
        const dataTimeoutMs = parsePositiveIntegerParam(params.get('dataTimeoutMs'))
        const priceTypes = parsePriceTypesParam(params.get('priceTypes'))

        // Fetch symbol maps and backend symbol definitions concurrently
        const [symbolMapRes, backendSymbolsRes] = await Promise.all([
          fetch('/symbols-map.json', { signal: controller.signal }),
          fetch('/backend-symbols.json', { signal: controller.signal }),
        ])
        if (!symbolMapRes.ok) throw new Error('Failed to fetch symbols-map.json')
        if (!backendSymbolsRes.ok) throw new Error('Failed to fetch backend-symbols.json')

        const [{ symbolFullNames, symbolShortNames }, backendSymbols] = await Promise.all([
          symbolMapRes.json() as Promise<SymbolMaps>,
          backendSymbolsRes.json() as Promise<Record<string, BackendSymbol>>,
        ])

        const canonicalizeSymbol = (symbol: string | undefined | null) =>
          resolveMappedShortSymbol(symbol, symbolFullNames, symbolShortNames)

        if (cancelled) return

        // Fetch per-symbol metadata in parallel
        const mergedSymbols: Record<string, MergedSymbolInfo> = {}
        const symbolInfoEntries = await Promise.all(
          Object.entries(symbolFullNames).map(async ([shortName, fullName]) => {
            const backendSymbol = backendSymbols[shortName]
            if (!backendSymbol) {
              return undefined
            }

            const symbolFileName = fullName.replace(/:/g, '_')
            const symbolinfoRes = await fetch(`/symbols/${symbolFileName}.json`, { signal: controller.signal })
            const rawSymbolInfo = symbolinfoRes.ok ? await symbolinfoRes.json() : undefined
            const symbolInfo = extractChartSymbolInfo(rawSymbolInfo)

            return [shortName, {
              backend: backendSymbol,
              symbolInfo,
            } satisfies MergedSymbolInfo] as const
          }),
        )

        for (const entry of symbolInfoEntries) {
          if (!entry) {
            continue
          }
          const [shortName, symbolInfo] = entry
          mergedSymbols[shortName] = symbolInfo
        }

        if (cancelled) return

        // Create WebSocket client with autoReconnect disabled during bootstrap.
        // Once initial connect succeeds, reconnect is enabled for fault tolerance.
        // This prevents the initial connection error from being masked by background reconnect attempts.
        const wsClient = new TournamentWSClient({
          tournamentId,
          userId,
          ticket,
          serverDomain,
          wsProtocol,
          autoReconnect: false,
        })

        if (cancelled) {
          wsClient.disconnect().catch(console.error)
          return
        }

        await wsClient.connect()

        // Enable auto-reconnect after successful bootstrap
        wsClient.setAutoReconnect(true)

        if (controller.signal.aborted) {
          wsClient.disconnect().catch(console.error)
          return
        }

        // Fetch tournament info and all operations in parallel
        const infoPromise = wsClient.info().catch(() => null)
        const allOpsPromise = wsClient.listAllOperations().catch(() => [])

        const info = (await infoPromise) as TournamentInfoResponse | null
        const allOps = await allOpsPromise

        if (controller.signal.aborted) {
          wsClient.disconnect().catch(console.error)
          return
        }

        // Check for missing symbols from shipped catalog (diagnostic pass)
        const missingSymbols: string[] = []
        if (info?.symbols && info.symbols.length > 0) {
          for (const serverSymbol of info.symbols) {
            const canonicalSymbol = canonicalizeSymbol(serverSymbol.symbol) || serverSymbol.symbol
            if (!mergedSymbols[canonicalSymbol]) {
              missingSymbols.push(serverSymbol.symbol)
            }
          }
          if (missingSymbols.length > 0) {
            console.error(
              '[useTournamentConnection] Tournament symbols missing from shipped catalog:',
              missingSymbols,
              'Available symbols:',
              Object.keys(mergedSymbols)
            )
          }
        }

        // Override conversion metadata with tournament-specific info
        if (info?.symbols && info.symbols.length > 0) {
          for (const serverSymbol of info.symbols) {
            const canonicalSymbol = canonicalizeSymbol(serverSymbol.symbol) || serverSymbol.symbol
            const merged = mergedSymbols[canonicalSymbol]
            if (!merged) {
              // This will be surfaced explicitly in console.error above
              continue
            }

            const conversionType =
              serverSymbol.conversionType === 'MULTIPLY' || serverSymbol.conversionType === 'DIVIDE'
                ? serverSymbol.conversionType
                : ''

            mergedSymbols[canonicalSymbol] = {
              ...merged,
              backend: {
                ...merged.backend,
                conversionSymbol: serverSymbol.conversionSymbol || undefined,
                conversionType,
              },
            }
          }
        }

        // Build allowed symbols from tournament info
        let allowedShortNames: Set<string> = new Set()

        const playerPrimary = canonicalizeSymbol(info?.playerPrimarySymbols?.[userId])
        const playerSecondary = canonicalizeSymbol(info?.secondarySymbol)

        if (playerPrimary) {
          allowedShortNames.add(playerPrimary)
        }
        if (playerSecondary && playerSecondary !== playerPrimary) {
          allowedShortNames.add(playerSecondary)
        }

        // Fallback to all tournament symbols if no explicit primary/secondary
        if (allowedShortNames.size === 0 && info?.symbols && info.symbols.length > 0) {
          info.symbols.forEach((s) => {
            const canonicalSymbol = canonicalizeSymbol(s.symbol)
            if (canonicalSymbol) {
              allowedShortNames.add(canonicalSymbol)
            }
          })
        }

        // Graceful degradation if no symbols resolved
        if (allowedShortNames.size === 0) {
          console.warn('[useTournamentConnection] No allowed symbols resolved from tournament info, using all available symbols')
          Object.keys(symbolFullNames).forEach((shortName) => allowedShortNames.add(shortName))
        }

        // Determine initial symbol
        const firstAllowedSymbol = Array.from(allowedShortNames)[0] || 'EUR/USD'
        const resolvedSymbol = (() => {
          if (symbolParam) {
            const normalizedSymbolParam = canonicalizeSymbol(symbolParam) || symbolParam

            if (allowedShortNames.has(normalizedSymbolParam)) {
              return normalizedSymbolParam
            } else {
              console.warn(
                `[useTournamentConnection] URL symbol param "${symbolParam}" resolved to "${normalizedSymbolParam}" but is not in allowed symbols, falling back to "${firstAllowedSymbol}"`,
              )
              return firstAllowedSymbol
            }
          }
          return (
            playerPrimary ||
            playerSecondary ||
            canonicalizeSymbol(info?.symbols?.[0]?.symbol) ||
            firstAllowedSymbol
          )
        })()

        // Create operation store
        const store = new OperationStore()
        if (cancelled) {
          wsClient.disconnect().catch(console.error)
          return
        }

        // Filter operations to local player only before hydration
        const localPlayerOps = filterLocalPlayerOperations(allOps, userId)
        store.dispatch({ type: 'HYDRATE', operations: localPlayerOps })

        if (controller.signal.aborted) {
          wsClient.disconnect().catch(console.error)
          return
        }

        // Wire WS events to store
        const unsubscribeTournamentEvent = wsClient.on('TOURNAMENT_EVENT', (msg: any) => {
          const event = msg.payload
          // Filter live events to local player (except TOURNAMENT_CLOSED which affects all)
          if (isLocalPlayerEvent(event, userId)) {
            store.dispatch({ type: 'TOURNAMENT_EVENT', event })
          } else if (event.type !== 'TOURNAMENT_CLOSED') {
            chartDebugLog('tournament', 'ignoring non-local event', { type: event.type })
          }
        })

        const unsubscribePriceUpdated = wsClient.on('PRICE_UPDATED', (msg: any) => {
          const { symbol: shortName, price } = msg.payload
          const numPrice = Number(price)
          store.dispatch({ type: 'PRICE_UPDATED', symbol: shortName, price: numPrice })
        })

        // Wire fast-forward events (Task 2.3 - enforce sequence: snapshot -> barrier -> sync -> clear -> repaint)
        // Sequence preservation ensures:
        // - Store receives new replay state before any chart mutations
        // - Barrier is set before candle refetch and application
        // - Sync completes before barrier is cleared (so pre-jump ticks are properly filtered)
        // - Chart refresh happens after all data is ready
        const unsubscribeFastForwardResponse = wsClient.on('FAST_FORWARD_RESPONSE', (msg: any) => {
          const normalized = normalizeFastForwardResponse(msg.payload, userId)
          if (!normalized) {
            console.error('[useTournamentConnection] Failed to normalize fast-forward response', msg.payload)
            return
          }

          // 1. Dispatch fast-forward snapshot to store (updates currentReplayTime for viewport positioning)
          store.dispatch({ type: 'FAST_FORWARD_RESPONSE', data: normalized })

          // 2. Trigger DXChart remount — this forces a full widget destroy + recreate cycle,
          // which re-initializes the data feed and loads fresh history from the resolver.
          // The viewport is automatically positioned to currentReplayTime in handleApiCreated.
          // This replaces the previous complex sync sequence (barrier → refetch → clear → repaint)
          // which couldn't reliably deliver bulk historical candles to DXChart's subscription callback.
          chartRemountKeyRef.current += 1
          setResult((prev) => ({ ...prev, chartRemountKey: chartRemountKeyRef.current }))

          chartDebugLog('fastForward', 'triggered chart remount', { key: chartRemountKeyRef.current })
        })

        const unsubscribeFastForwardError = wsClient.on('FAST_FORWARD_ERROR', (msg: any) => {
          // Error is observable but non-mutating (per design decision 5)
          console.warn('[useTournamentConnection] Fast-forward error received', msg.payload)
        })

        wsUnsubscribesRef.current = [
          unsubscribeTournamentEvent,
          unsubscribePriceUpdated,
          unsubscribeFastForwardResponse,
          unsubscribeFastForwardError,
        ]

        // Initialize reconciliation manager
        const reconciliationManager = new ReconciliationManager(wsClient, store, {
          onMetrics: (metrics) => {
            chartDebugLog('reconciliation', 'metrics updated', { count: metrics.length })
          },
          localUserId: userId,
        })
        if (cancelled) {
          reconciliationManager.destroy()
          wsClient.disconnect().catch(console.error)
          return
        }

        reconciliationManager.attach()
        reconciliationManager.attachToParseErrors(wsClient as any)

        // Filter symbol maps to allowed symbols only
        const filteredSymbolFullNames: Record<string, string> = {}
        const filteredSymbolShortNames: Record<string, string> = {}
        const filteredMergedSymbols: Record<string, MergedSymbolInfo> = {}

        for (const shortName of allowedShortNames) {
          if (symbolFullNames[shortName]) {
            filteredSymbolFullNames[shortName] = symbolFullNames[shortName]
          }
          const fullName = symbolFullNames[shortName]
          if (fullName && symbolShortNames[fullName]) {
            filteredSymbolShortNames[fullName] = symbolShortNames[fullName]
          }
          if (mergedSymbols[shortName]) {
            filteredMergedSymbols[shortName] = mergedSymbols[shortName]
          }
        }

        // Create providers
        const providerDependencies = createTournamentProviders({
          wsClient,
          store,
          reconciliationManager,
          symbolShortNames: filteredSymbolShortNames,
          symbolFullNames: filteredSymbolFullNames,
          mergedSymbols: filteredMergedSymbols,
          useDemoData,
          startDate: info?.startTime,
          endDate: cutoff || info?.endTime,
          symbol: resolvedSymbol,
          dataTimeoutMs,
          priceTypes,
          preloadedData: info?.initialChartData,
          tournamentType: info?.type,
          isHistorical: queueType === 'Historical',
          localUserId: userId,
        })

        if (cancelled) {
          reconciliationManager.destroy()
          wsClient.disconnect().catch(console.error)
          return
        }

        // Return the full dependencies object from createTournamentProviders
        // which includes all providers and configuration
        const dependencies = providerDependencies

        if (!cancelled) {
          setResult({
            dependencies,
            error: null,
            isReady: true,
            chartRemountKey: 0,
          })
        }
      } catch (err) {
        if (!cancelled) {
          const errorObj: AppError = {
            type: err instanceof Error ? err.constructor.name : 'Error',
            message: err instanceof Error ? err.message : 'Unknown error',
            originalError: err instanceof Error ? err : undefined,
          }
          setResult({
            dependencies: null,
            error: errorObj,
            isReady: false,
            chartRemountKey: 0,
          })
        }
      }
    }

    const initTimer = window.setTimeout(() => {
      void initializeConnection()
    }, 0)

    // Cleanup function
    return () => {
      cancelled = true
      clearTimeout(initTimer)
      controller.abort()

      // Cleanup will be done when the hook unmounts
    }
  }, [])

  // Cleanup on unmount (Task 1.3)
  useEffect(() => {
    return () => {
      const dependencies = result.dependencies
      if (!dependencies) return

      const controller = controllerRef.current

      // Abort pending fetches
      controller.abort()

      // Unsubscribe from websocket events
      wsUnsubscribesRef.current.forEach((unsubscribe) => {
        if (unsubscribe) {
          unsubscribe()
        }
      })
      wsUnsubscribesRef.current = []

      // Disconnect WebSocket
      dependencies.wsClient.disconnect().catch(console.error)

      // Destroy reconciliation manager
      dependencies.reconciliationManager.destroy()

      // Unsubscribe from store
      if (unsubscribeStoreRef.current) {
        unsubscribeStoreRef.current()
        unsubscribeStoreRef.current = null
      }
    }
  }, [result.dependencies])

  return result
}
